// ==========================================================================
// lib/pwa.ts — the home-screen app's updates (FF-058, 10 Oct 2026)
//   James: "Add a update app function in the settings / control panel …
//   makes updating easier when it's saved as an app on home screen."
//   The app updates itself when it notices a new version (registerType
//   autoUpdate), but an iPhone home-screen app can sit open for days without
//   looking. Update app asks now; if there's a new version it installs and
//   the app reloads. Fresh start clears the app's saved copy and reloads —
//   for when something looks stuck. Neither touches any family data (that
//   lives in Supabase, never in the app's saved copy).
// ==========================================================================
import { registerSW } from "virtual:pwa-register";

let reg: ServiceWorkerRegistration | undefined;
const updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_url, r) {
    reg = r;
    // Also look once an hour while the app is open.
    if (r) setInterval(() => { void r.update(); }, 60 * 60 * 1000);
  },
});

/** When this copy of the app was built (vite.config.ts defines it). */
export const BUILT_AT = new Date(__BUILD_TIME__);

export type UpdateResult = "updating" | "latest" | "not-installed";

/** Asks the server for a newer version. "updating" = the app is about to reload itself. */
export async function checkForUpdate(): Promise<UpdateResult> {
  if (!("serviceWorker" in navigator) || !reg) return "not-installed";
  await reg.update();
  if (reg.installing || reg.waiting) {
    void updateSW(true);
    // The new version takes over and the page reloads; this is a backstop.
    setTimeout(() => window.location.reload(), 8000);
    return "updating";
  }
  return "latest";
}

/** Clears the app's saved copy (not the family's data) and loads it fresh. */
export async function freshStart() {
  try {
    if ("serviceWorker" in navigator) for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister();
    if ("caches" in window) for (const k of await caches.keys()) await caches.delete(k);
  } finally {
    window.location.reload();
  }
}
